import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PageHeaderMeta } from '@/components/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogAddress,
    getCatalogCoverLots,
    getCatalogLotsListed,
    getCatalogPublishDateTs,
    getCatalogSellerId,
    getCatalogStartTime,
    getCatalogStatus,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getBaseUrl } from '@/redux/modules/config';
import { getSeller } from '@/redux/modules/seller';
import { EmptyCoverLot } from '@/types/CoverLot';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { formatHyphenatedDateOnly, shortFormatDate } from '@/utils/dates';
import { escapeQuotes } from '@/utils/escapeQuotes';
import { isEmpty } from '@/utils/isEmpty/isEmpty';
import { parseQueryParams } from '@/utils/queryParams';
import { safelyEscapeText } from '@/utils/safelyRender';
import { getCatalogUrl, getMediumItemImageUrl, getSellerUrl, mobileLogo } from '@/utils/urls';

type Props = {
    catalogId: number;
};

const buildCanonicalQuery = (query: any) => {
    const parts = [];
    if (query.page) {
        parts.push(query.page);
    }
    return parts.length ? `?${parts.join('&')}` : '';
};

const CatalogPageHeaderMeta = ({ catalogId }: Props) => {
    const baseUrl = useAppSelector(getBaseUrl);
    const address = useAppSelector((state) => getCatalogAddress(state, catalogId));
    const coverLots = useAppSelector((state) => getCatalogCoverLots(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const lotsListed = useAppSelector((state) => getCatalogLotsListed(state, catalogId));
    const publishDateTs = useAppSelector((state) => getCatalogPublishDateTs(state, catalogId));

    const seller = useAppSelector((state) => getSeller(state, sellerId));
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const queryParams = parseQueryParams(search);
    const intl = useIntl();
    const { formatMessage } = intl;

    // Check if search matches pattern: ? followed by any number
    const shouldRedirectSearch = /^\?\d+$/.test(search);

    // If search is just ?number, redirect to URL without search params
    useEffect(() => {
        if (shouldRedirectSearch) {
            navigate(pathname, { replace: true });
        }
    }, [shouldRedirectSearch, pathname, navigate]);

    const pastAuction = catalogStatus === AuctionStatus.Done;

    const { address1, address2, city, country: countryCode, lat, lng, postalCode, state: stateCode } = address;
    const displayAddress = address2 ? `${address1} ${address2}` : `${address1}`;
    const safeDisplayAddress = safelyEscapeText(displayAddress);

    const {
        address: sellerAddress,
        address2: sellerAddress2,
        city: sellerCity,
        country: sellerCountry,
        name: sellerName,
        stateCode: sellerStateCode,
        zip,
    } = seller;

    const sellerDisplayAddress = sellerAddress2 ? `${sellerAddress} ${sellerAddress2}` : `${sellerAddress}`;
    const safeSellerDisplayAddress = safelyEscapeText(sellerDisplayAddress);
    const [first = EmptyCoverLot, second = EmptyCoverLot] = coverLots;

    let imageUrl = getMediumItemImageUrl(sellerId, catalogId, first.itemId, 1, first.imageVersion) || mobileLogo;

    const safeCatalogTitle = safelyEscapeText(catalogTitle);

    const sellerLocation = stateCode || countryCode || '';

    let coverString = 'featuring ';
    if (coverLots.length) {
        if (first?.title || second?.title) {
            if (first?.title) {
                coverString += `${first.title}, `;
            }
            if (second?.title) {
                coverString += `${second.title}`;
            }
        }
    }

    const shortDateDay = shortFormatDate(saleStartTs);
    const hyphenatedDate = formatHyphenatedDateOnly(saleStartTs);
    const upcomingDescription = !coverLots.length
        ? formatMessage(
              { id: 'catalog_upcoming_description' },
              {
                  catalogTitle: safeCatalogTitle,
                  date: shortDateDay,
                  lotsListed,
                  sellerLocation,
                  sellerName,
              }
          )
        : formatMessage(
              { id: 'catalog_upcoming_description_with_results' },
              {
                  catalogTitle: safeCatalogTitle,
                  coverLotsString: coverString,
                  date: shortDateDay,
                  lotsListed,
                  sellerLocation,
                  sellerName,
              }
          );
    const pastDescription = formatMessage(
        { id: 'catalog_past_description' },
        {
            catalogTitle: safeCatalogTitle,
            date: shortDateDay,
            lotsListed,
            sellerLocation,
            sellerName,
        }
    );
    const description = !pastAuction ? upcomingDescription : pastDescription;
    const safeDescription = safelyEscapeText(description);

    let title = pastAuction
        ? formatMessage(
              { id: 'catalog_past_title' },
              {
                  catalogTitle: safeCatalogTitle,
                  hyphenatedDate,
                  lotsListed,
                  sellerLocation,
                  sellerName,
              }
          )
        : formatMessage(
              { id: 'catalog_upcoming_title' },
              {
                  catalogTitle: safeCatalogTitle.toLowerCase().includes('auction')
                      ? safeCatalogTitle
                      : `${safeCatalogTitle} Auction`,
                  date: shortFormatDate(saleStartTs),
                  sellerLocation,
                  sellerName,
              }
          );

    const { page } = queryParams;

    if (page > 1) {
        title += intl.formatMessage({ id: 'page_number_and_title' }, { page });
    }

    title = useAddNotificationCountToTitle(title);

    if (isEmpty(seller)) {
        return null;
    }

    const catalogPath = getCatalogUrl(catalogId, catalogTitle);
    const canonical = `${baseUrl}${catalogPath}${buildCanonicalQuery(queryParams)}`;

    let locationStr = '';
    if (Boolean(lat) && lng) {
        locationStr = `,
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": ${lat},
                "longitude": ${lng}
            }`;
    }

    const ts = saleStartTs * 1000;
    const startDate = new Date(ts);
    const endDate = new Date(ts + 14400000);
    const url = `${baseUrl}${catalogPath}`;
    const availability = !pastAuction ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
    const sellerUrl = `${baseUrl}${getSellerUrl(sellerId, sellerName)}`;
    const isNotPublished = !publishDateTs && catalogStatus !== AuctionStatus.Done;

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            imageUrl={imageUrl}
            title={title}
        >
            {isNotPublished && (
                <meta
                    content="noindex, nofollow"
                    name="robots"
                />
            )}

            <script type="application/ld+json">
                {`{
                        "@context": "https://schema.org",
                        "@type": "SaleEvent",
                        "description": "${escapeQuotes(safeDescription)}",
                        "name": "${escapeQuotes(safeCatalogTitle)}",
                        "image": "${imageUrl}",
                        "startDate": "${startDate.toISOString()}",
                        "endDate": "${endDate.toISOString()}",
                        "url": "${url}",
                        "location": {
                            "@type": "Place",
                            "name": "${sellerName}",
                            "address": {
                                "@type": "PostalAddress",
                                "addressCountry": "${countryCode}",
                                "addressLocality": "${city}",
                                "addressRegion": "${stateCode}",
                                "streetAddress": "${escapeQuotes(safeDisplayAddress)}",
                                "postalCode": "${postalCode}"
                            }${locationStr}
                        },
                        "offers": {
                            "availability": "${availability}",
                            "price": 0,
                            "priceCurrency": "USD",
                            "validFrom": "${startDate.toISOString()}",
                            "url": "${url}"
                        },
                        "organizer": {
                            "@type": "organization",
                            "address": {
                                "@type": "PostalAddress",
                                "addressCountry": "${sellerCountry}",
                                "addressLocality": "${sellerCity}",
                                "addressRegion": "${sellerStateCode}",
                                "streetAddress": "${escapeQuotes(safeSellerDisplayAddress)}",
                                "postalCode": "${zip}"
                              },
                            "name": "${sellerName}"
                        },
                        "performer": {
                            "@type": "Organization",
                            "name": "${sellerName}"
                        }
                    }`}
            </script>
            <script type="application/ld+json">
                {`{
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [{
                            "@type": "ListItem",
                            "item": {
                                "@id":"${baseUrl}",
                                "name":"Home"
                            },
                            "position": 1
                        },
                        {
                            "@type": "ListItem",
                            "item": {
                                "@id":"${baseUrl}/auctioneer/directory/",
                                "name":"Auction House Directory"
                            },
                            "position": 2
                        },
                        {
                                "@type": "ListItem",
                                "item": {
                                    "@id":"${sellerUrl}",
                                    "name":"${sellerName}"
                                },
                                "position": 3
                        },
                        {
                            "@type": "ListItem",
                            "item": {
                                "@id": "${canonical}",
                                "name":"${escapeQuotes(title)}"
                            },
                            "position": 4
                        }]
                    }`}
            </script>
        </PageHeaderMeta>
    );
};
// }

export default CatalogPageHeaderMeta;
